import React from "react";
import Checkbox from "@visiblethread/react-components/dist/components/Checkbox";
import { checkedTypes } from "constants/types";
import { ReactComponent as PlusIcon } from "icons/plus-grey.svg";
import { ReactComponent as MinusIcon } from "icons/minus-grey.svg";
import ProgressBar from "components/ProgressBar";
import PropTypes from "prop-types";
import { categoryProps, folderBy } from "layout/dictionarySearch/constants";
import { useDispatch, useSelector } from "react-redux";
import {
  setCategoryCheckState,
  setExpandedTo,
} from "slices/dictionarySearch/dictionarySearchSlice";
import Gradient from "components/Gradient";
import { dictionaryDefaultColour } from "constants/dictionary";
import { TooltipTrigger } from "@visiblethread/react-components/dist/components/Tooltip";

const Category = ({
  category,
  maxOccurrences,
  docId,
  alignCollapseButtonToRight = false,
}) => {
  const dispatch = useDispatch();
  const by = useSelector((state) => state.dictionarySearch.config.folderBy);
  const handleExpandCollapse = () => {
    dispatch(
      setExpandedTo({
        index: category.index,
        expanded: !category.expanded,
        docId,
      })
    );
  };
  const noOccurrences =
    category.occurrences === undefined || category.occurrences === 0;

  const handleCheck = () => {
    const newState =
      category.checkState === undefined ||
      category.checkState === checkedTypes.EMPTY ||
      category.checkState === checkedTypes.INDETERMINATE
        ? checkedTypes.CHECKED
        : checkedTypes.EMPTY;

    dispatch(
      setCategoryCheckState({
        index: category.index,
        checkedState: newState,
        docId,
      })
    );
  };

  const getExpandCollapseIcon = () => {
    const props = {
      width: 8,
      height: 8,
      className: "plus_minus_icon",
    };
    return category.expanded ? (
      <MinusIcon {...props} />
    ) : (
      <PlusIcon {...props} />
    );
  };

  const getCategoryName = () => {
    const text = category?.items?.length === 1 ? "Item" : "Items";
    return (
      <TooltipTrigger content={category.description}>
        <span>{`${category.name} (${
          category?.items?.length || 0
        } ${text})`}</span>
      </TooltipTrigger>
    );
  };

  const frequency = () => {
    return by === folderBy.folder ? (
      <>
        <ProgressBar
          current={category.occurrences}
          max={maxOccurrences}
          width="50px"
          height="5px"
        />
        <span>{category.occurrences > 0 ? category.occurrences : ""}</span>
      </>
    ) : (
      <Gradient
        content={category.occurrences}
        maxOccurrences={maxOccurrences}
      />
    );
  };

  const categoryColour = () => (
    <div
      className={`category__text__button__colour${
        noOccurrences ? " disabled" : ""
      }`}
      data-testid="category-colour"
      style={{ background: category.colour }}
    />
  );

  const text = () => (
    <>
      <Checkbox
        state={
          category.checkState && category.occurrences
            ? category.checkState
            : checkedTypes.EMPTY
        }
        handleChange={handleCheck}
        disabled={noOccurrences}
        id="category_cb"
        value="category_cb"
      />
      <button
        onClick={handleExpandCollapse}
        type="button"
        className={`category__text__button${noOccurrences ? " greyed" : ""}`}
      >
        {getCategoryName()}
        {category.colour !== dictionaryDefaultColour && categoryColour()}
        <div
          className="category__text__button__icon"
          style={{
            marginLeft: alignCollapseButtonToRight ? "auto" : "",
          }}
        >
          {getExpandCollapseIcon()}
        </div>
      </button>
    </>
  );

  return (
    <div
      className={`category${noOccurrences ? " greyed" : ""}`}
      data-testid="category"
    >
      <div className="category__text">{text()}</div>
      <div className="category__frequency">{frequency()}</div>
    </div>
  );
};

export default Category;

Category.propTypes = {
  category: PropTypes.shape(categoryProps),
  maxOccurrences: PropTypes.number,
  docId: PropTypes.number,
  alignCollapseButtonToRight: PropTypes.bool,
};
